import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { AICA_ADD_TO_WORKSPACE, AICA_WORKSPACE_UNSAVED_EDITS_PROMPT } from '../../utils/constants/mixpanelEvents/investorEvents';
import type { UnsavedEditsPromptAction } from '../DocumentViewer/useUnsavedEditsGuard';
import { classifyAddable } from './addFileToReports';
import { isEditedWorkspaceSnapshot } from './editedWorkspaceSnapshot';
import { isReadOnlyAddBand } from '../SheetEditor/sheetSizeGuard';

/** Where an Add to Workspace started (RECUR-27850). FILE_VIEWER is the shared
 *  docId viewer, which isn't one of the three surfaces the ticket names. */
export type WorkspaceSource = 'MAGIC_UPLOAD' | 'DATA_ROOM' | 'ASK_AICA' | 'FILE_VIEWER';
/** PDF can be added (stored as-is) but never edited. */
export type WorkspaceFileKind = 'SPREADSHEET' | 'DOCUMENT' | 'PDF';

interface TrackingUser {
    userId?: string;
    accountType?: string;
}

export const workspaceFileKind = (fileName: string, fileType?: string): WorkspaceFileKind | undefined => {
    const kind = classifyAddable(fileName, fileType);
    if (kind === 'sheet') return 'SPREADSHEET';
    if (kind === 'docx' || kind === 'txt') return 'DOCUMENT';
    if (kind === 'pdf') return 'PDF';
    return undefined;
};

const standardProperties = (user: TrackingUser | undefined, organizationId: string | undefined) => ({
    organizationId,
    pnoPlatform: getSubdomain(),
    userId: user?.userId,
    userRole: user?.accountType,
});

export interface AddToWorkspaceTracking {
    source: WorkspaceSource;
    fileName: string;
    fileType?: string;
    organizationId?: string;
    user?: TrackingUser;
}

export interface AddToWorkspaceTracker {
    /** Original-file size, once the host has fetched it (unedited adds). */
    setFileSizeBytes: (bytes: number) => void;
    succeeded: () => void;
    /** Safe to call unconditionally (e.g. in `finally`): a no-op once the add succeeded. */
    failed: () => void;
}

/**
 * Starts tracking one Add to Workspace. Exactly one `AICA_Add_To_Workspace`
 * event is sent (source, fileKind, wasEdited, outcome, durationMs, and
 * fileSizeBytes when known) — hosts call `succeeded()` after the save and
 * `failed()` in `finally`, which also covers their early-return fetch errors.
 * `payload` is what the drawer handed the host (edited snapshot / read-only
 * band / nothing, or a stray click event).
 */
export const startAddToWorkspaceTracking = (tracking: AddToWorkspaceTracking, payload: unknown): AddToWorkspaceTracker => {
    const startedAt = Date.now();
    let fileSizeBytes = isReadOnlyAddBand(payload) ? payload.rawBytes.byteLength : undefined;
    let finished = false;
    const finish = (outcome: 'SUCCESS' | 'FAILED') => {
        if (finished) return;
        finished = true;
        // Analytics must never be the reason an add (or its error toast) breaks.
        try {
            Mixpanel.track(AICA_ADD_TO_WORKSPACE, {
                source: tracking.source,
                fileKind: workspaceFileKind(tracking.fileName, tracking.fileType),
                wasEdited: isEditedWorkspaceSnapshot(payload),
                outcome,
                durationMs: Date.now() - startedAt,
                ...(fileSizeBytes != null ? { fileSizeBytes } : {}),
                ...standardProperties(tracking.user, tracking.organizationId),
            });
        } catch (err) {
            console.error('[workspaceAnalytics] AICA_Add_To_Workspace not sent', err);
        }
    };
    return {
        setFileSizeBytes: (bytes) => {
            fileSizeBytes = bytes;
        },
        succeeded: () => finish('SUCCESS'),
        failed: () => finish('FAILED'),
    };
};

export interface UnsavedEditsPromptTracking {
    source: WorkspaceSource;
    fileKind: WorkspaceFileKind;
    organizationId?: string;
    user?: TrackingUser;
}

/** `AICA_Workspace_Unsaved_Edits_Prompt` — how the close prompt was answered. */
export const trackUnsavedEditsPrompt = (action: UnsavedEditsPromptAction, tracking: UnsavedEditsPromptTracking): void => {
    try {
        Mixpanel.track(AICA_WORKSPACE_UNSAVED_EDITS_PROMPT, {
            action,
            source: tracking.source,
            fileKind: tracking.fileKind,
            ...standardProperties(tracking.user, tracking.organizationId),
        });
    } catch (err) {
        console.error('[workspaceAnalytics] AICA_Workspace_Unsaved_Edits_Prompt not sent', err);
    }
};
